import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'dd.MM.yyyy', { locale: de });
};

export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'dd.MM.yyyy HH:mm', { locale: de });
};

export const formatDateForInput = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
};

export const parseDate = (dateString: string): Date => {
  return parseISO(dateString);
};

/**
 * Calendar-day helpers.
 *
 * The API stores schedule dates as plain dates and serialises them as midnight
 * UTC ("2026-10-19T00:00:00.000Z"). Converting those to a local Date and
 * comparing with toDateString() shifts the day in some timezones, and sending
 * a local Date to the API leaks the time of day into date-only queries. So the
 * client talks to the API in "YYYY-MM-DD" and compares calendar days as strings.
 */

/** Local calendar day of a Date as "YYYY-MM-DD" - the day shown in the UI. */
export const toDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');

/** Calendar day of a date value received from the API. */
export const datumToDateKey = (datum: string | Date): string =>
  typeof datum === 'string' ? datum.slice(0, 10) : toDateKey(datum);

/** True when an API date value falls on the given (local) calendar day. */
export const isSameDay = (datum: string | Date, date: Date): boolean =>
  datumToDateKey(datum) === toDateKey(date);

/** True when the (local) calendar day lies within an API date range, inclusive. */
export const isDateWithin = (date: Date, start: string | Date, end: string | Date): boolean => {
  const key = toDateKey(date);
  return key >= datumToDateKey(start) && key <= datumToDateKey(end);
};
